import React, { useContext, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useClaimAllMaterialsMutation,
  usePutAllFactoryMutation,
} from '@api/queries/factory';
import { gameItemOrder, materialsList } from '@common/constants';
import { useDisabledDelay } from '@common/hooks/disabled-delay';
import { firebaseLogCollectResource } from '@common/lib/firebaseLog';
import CardsResources from '@components/Resources/TabsBody/Card/CardsResources';
import {
  BlueButton,
  GreyBlurButton,
  MainButton,
} from '@components/ui/Button/Button';
import { selectFactoriesAsObject } from '@redux/selectors/factoryReducer';
import { useAppSelector } from '@redux/store';

import { ToastNotification } from '@/common/components/ToastNotification/ToastNotification';
import { useCheckStorageOverflow } from '@/common/hooks/check-storage-overflow';
import { useCheckVIPstatus } from '@/common/hooks/checkVIPstatus';
import { playSound } from '@/common/lib/soundEffect';
import { ModalContext } from '@/components/Modals/Modal/config';
import { StringSmall } from '@/components/Typography/Typography';

import stockPng from '/img/tabbar/office.png';
import stockWebp from '/img/tabbar/office.webp';

const PageBody: React.FC = () => {
  const { t } = useTranslation();
  const isFirstVisit = useAppSelector((state) => state.user.isFirstVisit);
  const [claimAllMaterials, { isLoading }] = useClaimAllMaterialsMutation();
  const checkStockOverflow = useCheckStorageOverflow();
  const factoriesObject = useAppSelector(selectFactoriesAsObject);
  const [disabled, setDisabled] = useDisabledDelay();
  const factoriesNextToBuild = useAppSelector(
    (state) => state.factory.nextToBuild,
  );
  const storage = useAppSelector((state) => state.user.user?.storage);
  const previousValueRef = useRef(storage);
  const { openModal } = useContext(ModalContext);
  const statusVIP = useCheckVIPstatus();
  const [putAllFactory, { isLoading: loadingAll }] = usePutAllFactoryMutation();

  const onClaim = (): void => {
    claimAllMaterials()
      .unwrap()
      .then(({ storage }) => {
        checkStockOverflow(storage, gameItemOrder.material);
        ToastNotification(t('Materials sent to Stock'), {
          img: stockPng,
          webp: stockWebp,
        });
      })
      .catch((error) => {
        console.log(error);
        ToastNotification(
          t('Error: Failed to send resources'),
          undefined,
          'justify-center',
        );
      });
  };

  const onLoadAll = (): void => {
    putAllFactory()
      .unwrap()
      .then(() => {
        ToastNotification(t('All materials are loaded'), {
          img: stockPng,
          webp: stockWebp,
        });
      })
      .catch((error) => {
        console.log(error);
        // ToastNotification(
        //   t('Error: Failed to load materials'),
        //   undefined,
        //   'justify-center',
        // );
        ToastNotification(
          `${t('Error')} ${error.status}: ${error.data?.message}`,
          undefined,
          'justify-center',
        );
      });
  };

  const buyPaidVip = (): void => {
    if (statusVIP) return;
    openModal({
      renderKind: 'selectVipPrice',
      props: { type: 'paid' },
    });
  };

  useEffect(() => {
    if (previousValueRef.current !== storage && factoriesObject) {
      firebaseLogCollectResource(
        factoriesObject,
        storage,
        previousValueRef.current,
      );
      previousValueRef.current = storage;
    }
  }, [storage]);

  return (
    <div className="tabs__body">
      <div className="tabs__list">
        <>
          <CardsResources
            list={materialsList}
            buildsObject={factoriesObject}
            nextToBuild={factoriesNextToBuild || undefined}
            kind="material"
          />
        </>
      </div>
      <div className="flex gap-2">
        <MainButton
          onClick={() => {
            playSound('buttons/switch');
            setDisabled(true);
            onClaim();
          }}
          disabled={isLoading || disabled}
          inactive={disabled || isFirstVisit}
          isLoading={isLoading}
          text={t('Collect')}
        />
        {!statusVIP ? (
          <GreyBlurButton text="" onClick={buyPaidVip} className="flex gap-2">
            <img src="/img/VIP/vip.svg" alt="" />
            <StringSmall className="!font-[800] !text-[17px]">
              {t('Load')}
            </StringSmall>
          </GreyBlurButton>
        ) : (
          <BlueButton
            onClick={onLoadAll}
            disabled={loadingAll}
            inactive={loadingAll}
          >
            <StringSmall className="!font-[800] !text-[18px]">
              {t('Load')}
            </StringSmall>
          </BlueButton>
        )}
      </div>
    </div>
  );
};

export default PageBody;
